import { SearchableTree } from "@/shared-components/SearchableTree";
import { useCallback, useEffect, useState } from "react";
import { IPanelScope, ISearchResult, ITreeItem } from "@/interfaces";
import { DEFAULT_FILTER_KEY, GENERIC_ERROR_MESSAGE } from "@/constants";
import { findNodeByIdAsync, createTreeFetchCallback } from "@/utils/common";
import { FiltersService, SearchService } from "@/services";

import { SelectableBehavior } from "../SelectableBehaivor/SelectableBehavior";
import styles from "./FilterSearchableTree.module.scss";

interface InitialSelectedItem {
  id: string;
  auto_field?: string;
  name: string;
}

interface GenericSearchableTreeProps {
  scope: IPanelScope;
  initialSelectedItem?: InitialSelectedItem;
  onChange: (node: ITreeItem) => void;
}

export const FilterSearchableTree = ({
  scope,
  initialSelectedItem,
  onChange,
}: GenericSearchableTreeProps) => {
  const [treeItems, setTreeItems] = useState<ITreeItem[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [selectedItem, setSelectedItem] = useState<ITreeItem | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      let _selectedItem = undefined;
      if (initialSelectedItem?.id) {
        const selectedFolder = await FiltersService.fetchFilterFromServer(
          scope,
          initialSelectedItem.id,
        );

        if (initialSelectedItem.auto_field) {
          const autoFields = initialSelectedItem.auto_field.split(";");

          const name =
            autoFields[0] === "other_filter"
              ? "Other/Empty values"
              : autoFields[0];

          _selectedItem = selectedFolder.children.find((child) =>
            child.name.startsWith(name),
          );

          if (autoFields.length > 1) {
            _selectedItem = _selectedItem?.children.find((child) =>
              child.name.startsWith(autoFields[1]),
            );
          }
        } else {
          _selectedItem = selectedFolder;
        }
      }

      setSelectedItem(_selectedItem);
      await fetchFolders(_selectedItem);
    })();
  }, []);

  const buildAncestryTree = async (
    filters: ITreeItem[],
    _selectedItem: ITreeItem | undefined,
  ) => {
    if (!_selectedItem?.id) return;

    const ancestry = _selectedItem.serverObject.ancestry?.split("/") || [];

    for (let i = 0; i < ancestry.length; i++) {
      const ancestryId = ancestry[i];
      const filter = await FiltersService.fetchFilterFromServer(
        scope,
        ancestryId,
      );

      const fetchCallback = createTreeFetchCallback(scope);
      const currFilter = await findNodeByIdAsync(
        filters,
        ancestryId,
        fetchCallback,
      );

      if (currFilter) {
        currFilter.children = [...filter.children];
      }
    }
  };

  const fetchFolders = async (_selectedItem: ITreeItem | undefined) => {
    try {
      setIsLoading(true);

      const { filters } = await FiltersService.fetchFiltersFromServer(scope);

      await buildAncestryTree(filters, _selectedItem);
      setTreeItems([...filters]);
    } catch (err) {
      console.error("fetchFolders->Failure fetch folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNodeExpand = async (node: ITreeItem) => {
    if (node.children.length) return;

    node.extra.isLoading = true;
    node.extra.error = null;
    try {
      const filter = await FiltersService.fetchFilterFromServer(scope, node.id);

      node.extra.isLoading = false;

      const fetchCallback = createTreeFetchCallback(scope, () =>
        setTreeItems([...treeItems]),
      );
      const foundNode = await findNodeByIdAsync(
        treeItems,
        node.id,
        fetchCallback,
      );

      if (!foundNode) return;
      foundNode.children = [...filter.children];
    } catch (e) {
      console.error(`FolderSearchableTree=>handleNodeExpand`, e);
      node.extra.error = GENERIC_ERROR_MESSAGE;
    } finally {
      node.extra.isLoading = false;
      //setTreeItems([...treeItems]);
    }
  };

  const handleSearch = useCallback(
    async (criteria: string): Promise<ISearchResult | undefined> => {
      try {
        setIsLoading(true);
        return await SearchService.serverSearch(scope, criteria);
      } catch (err) {
        console.error("handleSearch->Failure search folders", err);
        setError(GENERIC_ERROR_MESSAGE);
      } finally {
        setIsLoading(false);
      }
    },
    [scope, setError], // Add dependencies here
  );

  const handleSelected = (node: ITreeItem | undefined) => {
    setSelectedItem(node);
    onChange(node?.serverObject || { id: DEFAULT_FILTER_KEY });
  };

  const getDisplayName = () => {
    return selectedItem?.name || "";
  };

  return (
    // ToDo: fix typescript error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <SelectableBehavior
      selectedItem={selectedItem}
      onClear={() => handleSelected(undefined)}
      value={getDisplayName()}
      placeholder={`All ${scope.module} (No filter)`}
    >
      <SearchableTree
        customClassName={styles.customSearchableTree}
        isLoading={isLoading}
        items={treeItems}
        error={error}
        selectedItem={selectedItem}
        scope={scope}
        handleSelected={handleSelected}
        handleNodeExpand={handleNodeExpand}
        handleSearch={handleSearch}
      />
    </SelectableBehavior>
  );
};
